import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LogOut, Plus } from 'lucide-react';

import { CourseCard } from '../components/ui/CourseCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/primitives/Dialog';
import { CourseProfileEditor } from '../studio/components/coursePersona/CourseProfileEditor';
import { CourseSeedWizard } from '../studio/components/course/CourseSeedWizard';
import {
  createEmptyCourseProfile,
  resolveCourseDisplayName,
} from '../studio/constants/courseProfileUi';
import { buildCourseDashboardPath } from '../studio/constants/courseStudioRoutes';
import {
  createCourseProfileId,
  getCourseProfiles,
  saveCourseProfile,
} from '../studio/services/courseProfileStorage';
import type { CourseProfile } from '../studio/types/courseProfile';
import { useSalaAuth } from '../auth/SalaAuthContext';
import { getSubjectById } from '../studio/constants/subjectCatalog';

export function StudioPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile, signOut } = useSalaAuth();
  const [courses, setCourses] = useState<CourseProfile[]>(() => getCourseProfiles());
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<CourseProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedDismissed, setSeedDismissed] = useState(false);

  const refreshCourses = useCallback(() => {
    setCourses(getCourseProfiles());
  }, []);

  useEffect(() => {
    refreshCourses();
  }, [profile?.uid, refreshCourses]);

  useEffect(() => {
    if (searchParams.get('seedCourses') === '1') {
      setSeedOpen(true);
      setSeedDismissed(false);
      const next = new URLSearchParams(searchParams);
      next.delete('seedCourses');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (courses.length === 0 && profile && !seedDismissed) {
      setSeedOpen(true);
    }
  }, [courses.length, profile, seedDismissed]);

  const openNewCourse = () => {
    const subjectId = profile?.subjectId?.trim();
    if (!subjectId) {
      setStatus('Configura la asignatura del docente antes de crear un curso.');
      return;
    }
    setDraft(
      createEmptyCourseProfile(subjectId, {
        schoolName: profile?.schoolName,
      }),
    );
    setStatus(null);
    setEditorOpen(true);
  };

  const handleSaveCourse = () => {
    if (!draft) return;
    if (!draft.level) {
      setStatus('Elige el nivel curricular.');
      return;
    }

    setSaving(true);
    try {
      const saved = saveCourseProfile({
        ...draft,
        id: draft.id || createCourseProfileId(),
        displayName: resolveCourseDisplayName(draft),
        schoolName: draft.schoolName || profile?.schoolName || '',
      });
      refreshCourses();
      setEditorOpen(false);
      setDraft(null);
      navigate(buildCourseDashboardPath(saved.id));
    } finally {
      setSaving(false);
    }
  };

  const subjectLabel = profile ? getSubjectById(profile.subjectId)?.name : null;

  return (
    <div className="studio-page w-full min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--studio-accent)]">
          Studio · Mis cursos
        </p>
        {profile ? (
          <div className="flex items-center gap-3 text-xs text-[var(--sala-ink-muted)]">
            <span>
              Hola, <strong className="text-[var(--sala-ink)]">{profile.displayName}</strong>
              {subjectLabel ? ` · ${subjectLabel}` : ''}
            </span>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 font-semibold transition hover:opacity-80"
              style={{ borderColor: 'var(--sala-border-strong)' }}
              onClick={() => void signOut().then(() => navigate('/'))}
            >
              <LogOut className="h-3.5 w-3.5" />
              Salir
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 max-w-3xl">
          <h1 className="font-display text-4xl text-[var(--sala-ink)] sm:text-5xl">
            Tus <span className="text-[var(--studio-accent)]">cursos</span>
          </h1>
          <p className="mt-3 text-[var(--sala-ink-muted)]">
            Cada curso agrupa materiales, planificación y evaluación con un contexto único para la IA.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {courses.length === 0 ? (
            <button
              type="button"
              className="studio-soft-btn-outline text-sm"
              onClick={() => setSeedOpen(true)}
            >
              Armar cursos por nivel
            </button>
          ) : null}
          <button type="button" className="sala-btn-hero text-sm shadow-sm" onClick={openNewCourse}>
            <Plus className="mr-1.5 inline h-4 w-4" strokeWidth={2.5} />
            Nuevo Curso
          </button>
        </div>
      </div>

      {status ? (
        <p className="mt-4 text-sm text-amber-700">{status}</p>
      ) : null}

      {courses.length === 0 ? (
        <div
          className="mt-10 rounded-3xl border border-dashed p-10 text-center"
          style={{ borderColor: 'var(--sala-border-strong)', background: 'var(--micelio-paper, #fff)' }}
        >
          <p className="font-display text-lg text-[var(--sala-ink)]">Aún no tienes cursos</p>
          <p className="mt-2 text-sm text-[var(--sala-ink-muted)]">
            Crea varios niveles de una vez, o agrega un curso suelto.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button type="button" className="sala-btn-hero text-xs" onClick={() => setSeedOpen(true)}>
              Armar cursos por nivel
            </button>
            <button type="button" className="sala-btn-outline text-xs" onClick={openNewCourse}>
              + Nuevo Curso
            </button>
          </div>
        </div>
      ) : (
        <div className="subject-grid mt-8">
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}

      <p className="mt-10 max-w-2xl text-sm text-[var(--sala-ink-dim)]">
        Actividades y motores en vivo en{' '}
        <Link
          to="/studio/herramientas"
          className="font-semibold text-[var(--sala-accent)] underline-offset-2 hover:underline"
        >
          Taller interactivo
        </Link>
        . Para crear material sin curso, usá <strong>Crear</strong> en el rail.
      </p>

      {profile ? (
        <CourseSeedWizard
          open={seedOpen}
          profile={profile}
          onClose={() => {
            setSeedOpen(false);
            setSeedDismissed(true);
          }}
          onCreated={() => {
            refreshCourses();
            setSeedDismissed(true);
          }}
        />
      ) : null}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo curso</DialogTitle>
            <DialogDescription>
              Define el perfil del grupo. La asignatura viene de tu perfil de Sala.
            </DialogDescription>
          </DialogHeader>
          {draft ? (
            <CourseProfileEditor
              profile={draft}
              onChange={setDraft}
              onSave={handleSaveCourse}
              onDelete={() => setEditorOpen(false)}
              isNew
              saving={saving}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
